import React, { useEffect, useRef, useState } from "react";
import { X, Gift, Sparkles, Flame, Coins, GraduationCap, Tag, LogIn, PartyPopper, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const KEY = "taven_spin";
const DISCOUNT_KEY = "taven_discount_code";
const GOLD_C = "#C9A24B", ORANGE_C = "#E2661F";
const CELEBRATION_CSS = `
@keyframes tavenBurst { 0%{transform:translate(-50%,-50%) scale(.2);opacity:0} 15%{opacity:1} 100%{transform:translate(calc(-50% + var(--tx)),calc(-50% + var(--ty))) scale(1) rotate(20deg);opacity:0} }
@keyframes tavenConfetti { 0%{transform:translateY(-10vh) rotate(0);opacity:1} 100%{transform:translateY(110vh) rotate(720deg);opacity:.9} }
.taven-burst{position:absolute;width:72px;height:72px;object-fit:cover;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,.35);border:2px solid #fff;animation:tavenBurst 2.2s cubic-bezier(.2,.7,.3,1) forwards}
.taven-confetti{position:absolute;top:-10vh;width:9px;height:14px;border-radius:2px;animation:tavenConfetti linear forwards}
`;
const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
export const getSpinDiscount = () => { try { return localStorage.getItem(DISCOUNT_KEY) || ""; } catch { return ""; } };

const sameDay = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.toDateString() === n.toDateString();
};

const shortLabel = (l) => (l.length > 12 ? l.replace(" Reward", "").replace(" Gift", "").replace(" Box", "") : l);

// TAVEN Spin & Save v2 — multi-reward wheel. Discounts apply to PRODUCTS ONLY.
export const SpinWheel = () => {
  const { user, authApi } = useAuth() || {};
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [segments, setSegments] = useState([]);
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [showcase, setShowcase] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const state = useRef(read());

  useEffect(() => {
    api.get("/promotions/spin/config").then(({ data }) => {
      if (!data.enabled) return;
      setEnabled(true);
      setSegments(data.segments || []);
      const s = state.current;
      if (!sameDay(s.lastSpin) && !sameDay(s.dismissedAt)) {
        const t = setTimeout(() => setOpen(true), 7500);
        return () => clearTimeout(t);
      }
    }).catch(() => {});
    api.get("/promotions/spin/showcase").then(({ data }) => setShowcase(data.products || [])).catch(() => {});
  }, []);

  const spunToday = sameDay(state.current.lastSpin);
  const count = segments.length || 1;
  const segDeg = 360 / count;
  const client = user && authApi ? authApi : api;

  const doSpin = async () => {
    if (spinning || spunToday || !count) return;
    setSpinning(true); setResult(null);
    try {
      const { data } = await client.post("/promotions/spin", { streak: state.current.streak || 0 });
      if (data.on_cooldown) {
        setSpinning(false);
        const next = { ...state.current, lastSpin: new Date().toISOString() };
        localStorage.setItem(KEY, JSON.stringify(next)); state.current = next;
        toast.message(data.message || "You've already claimed today's spin. Come back tomorrow!");
        return;
      }
      const prize = data.prize;
      const idx = prize.segment_index;
      const target = 360 * 6 + (360 - (idx * segDeg + segDeg / 2));
      setRotation((r) => r - (r % 360) + target);
      setTimeout(() => {
        setSpinning(false);
        setResult({ ...prize, streak: data.streak });
        const next = { lastSpin: new Date().toISOString(), streak: data.streak };
        localStorage.setItem(KEY, JSON.stringify(next));
        state.current = next;
        // Celebration: burst of REAL available products + a featured prize showcase item.
        if (showcase.length) setFeatured(showcase[Math.floor(Math.random() * showcase.length)]);
        setCelebrate(true);
        setTimeout(() => setCelebrate(false), 2600);
        if (prize.type === "DISCOUNT" && prize.code) {
          localStorage.setItem(DISCOUNT_KEY, prize.code);
          toast.success(`You won ${prize.percent}% OFF products! Applied at checkout.`);
        } else if (prize.credited && prize.coins) {
          toast.success(`+${prize.coins} TAVEN Coins added to your wallet!`);
        } else if (prize.credited && prize.type === "FREE_CLASS") {
          toast.success("Free Class voucher unlocked!");
        } else if (prize.requires_login) {
          toast.message("Sign in to claim this reward.");
        }
      }, 4200);
    } catch { setSpinning(false); toast.error("The wheel is resting — please try again."); }
  };

  const close = () => {
    setOpen(false);
    const s = { ...state.current, dismissedAt: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(s)); state.current = s;
  };

  const claimPrize = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      if (result?.spin_id) {
        await client.post("/promotions/spin/claim", { spin_id: result.spin_id });
      }
      if (result?.type === "DISCOUNT" && result?.code) {
        localStorage.setItem(DISCOUNT_KEY, result.code);
      }
      setOpen(false);
      // Open the winning product inside TAVEN (discount auto-applies at checkout).
      navigate(featured ? featured.url : "/shop");
    } catch {
      toast.error("Could not claim right now — please try again.");
    } finally { setClaiming(false); }
  };

  if (!enabled) return null;

  const conic = segments.map((s, i) => `${s.color} ${i * segDeg}deg ${(i + 1) * segDeg}deg`).join(", ");

  const RewardIcon = ({ type }) => {
    if (type === "DISCOUNT") return <Tag className="w-5 h-5 text-taven-gold" />;
    if (type === "FREE_CLASS") return <GraduationCap className="w-5 h-5 text-taven-gold" />;
    return <Coins className="w-5 h-5 text-taven-gold" />;
  };

  return (
    <>
      <style>{CELEBRATION_CSS}</style>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="spin-launcher"
          className="fixed left-4 bottom-4 z-50 flex items-center gap-2 bg-taven-black text-white pl-3 pr-4 py-2.5 shadow-lg border border-taven-gold/40 hover:bg-taven-orange transition-colors">
          <Gift className="w-4 h-4 text-taven-gold" /><span className="text-xs font-bold uppercase tracking-wider">Spin &amp; Win</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="spin-modal" onClick={close}>
          <div className="bg-white max-w-md w-full p-8 relative text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={close} data-testid="spin-close" className="absolute top-4 right-4 text-black/40 hover:text-black"><X className="w-5 h-5" /></button>
            <div className="flex items-center justify-center gap-2"><Sparkles className="w-5 h-5 text-taven-gold" /><h2 className="text-2xl font-black font-display">TAVEN Spin &amp; Win</h2></div>
            <p className="text-sm text-muted-foreground mt-1">Spin daily for <strong>product discounts, TAVEN Coins &amp; more</strong>. One spin per day.</p>

            <div className="relative mx-auto my-6" style={{ width: 280, height: 280 }}>
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10" style={{ width: 0, height: 0, borderLeft: "12px solid transparent", borderRight: "12px solid transparent", borderTop: "20px solid #E2661F" }} />
              <div data-testid="spin-wheel" className="rounded-full border-4 border-taven-black shadow-inner"
                style={{ width: 280, height: 280, background: `conic-gradient(${conic})`, transform: `rotate(${rotation}deg)`, transition: spinning ? "transform 4s cubic-bezier(0.15,0.9,0.25,1)" : "none" }}>
                {segments.map((s, i) => (
                  <div key={s.id} className="absolute left-1/2 top-1/2 origin-left text-[9px] font-black uppercase tracking-wide"
                    style={{ transform: `rotate(${i * segDeg + segDeg / 2}deg) translate(30px, -5px)`, width: 100, color: s.color === "#111111" ? "#fff" : "#111" }}>
                    {shortLabel(s.label)}
                  </div>
                ))}
              </div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border-4 border-taven-gold flex items-center justify-center"><Gift className="w-4 h-4 text-taven-black" /></div>
            </div>

            {!result ? (
              <button onClick={doSpin} disabled={spinning || spunToday} data-testid="spin-button"
                className="w-full bg-taven-orange text-white font-bold uppercase tracking-wider py-4 hover:bg-[#d9561a] transition-colors disabled:opacity-60">
                {spinning ? "Spinning…" : spunToday ? "Come back tomorrow" : "Spin the Wheel"}
              </button>
            ) : (
              <div data-testid="spin-result" className="mt-1">
                {celebrate && (
                  <div className="fixed inset-0 z-[80] pointer-events-none overflow-hidden" data-testid="spin-celebration">
                    {/* Confetti */}
                    {Array.from({ length: 40 }).map((_, i) => (
                      <span key={`c${i}`} className="taven-confetti"
                        style={{ left: `${Math.random() * 100}%`, background: [GOLD_C, ORANGE_C, "#fff"][i % 3],
                          animationDelay: `${Math.random() * 0.5}s`, animationDuration: `${1.6 + Math.random() * 1.2}s` }} />
                    ))}
                    {/* Product image burst */}
                    <div className="absolute left-1/2 top-1/2">
                      {showcase.slice(0, 10).map((p, i) => {
                        const ang = (i / 10) * Math.PI * 2;
                        const dist = 160 + (i % 3) * 40;
                        return (
                          <img key={p.handle} src={p.image} alt="" className="taven-burst"
                            style={{ "--tx": `${Math.cos(ang) * dist}px`, "--ty": `${Math.sin(ang) * dist}px`, animationDelay: `${i * 0.04}s` }} />
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="p-4 bg-taven-gold/10 border border-taven-gold">
                  <div className="flex items-center justify-center gap-2"><PartyPopper className="w-5 h-5 text-taven-orange" /><RewardIcon type={result.type} /><p className="text-lg font-black font-display">You won: {result.label}!</p></div>
                  {result.type === "DISCOUNT" && (
                    <p className="text-sm text-muted-foreground mt-1">Code <strong className="text-taven-gold">{result.code}</strong> auto-applies to your TAVEN product order at checkout.</p>
                  )}
                  {(result.type === "COINS" || result.type === "MYSTERY") && result.credited && (
                    <p className="text-sm text-muted-foreground mt-1"><strong className="text-taven-gold">{result.coins} TAVEN Coins</strong> added to your wallet.</p>
                  )}
                  {result.type === "FREE_CLASS" && result.credited && (
                    <p className="text-sm text-muted-foreground mt-1">A <strong className="text-taven-gold">Free Class voucher</strong> is saved to your account.</p>
                  )}
                  {result.requires_login && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground">{result.type === "FREE_CLASS" ? "A Free Class voucher" : `${result.coins} TAVEN Coins`} is waiting for you.</p>
                      <Link to="/login" data-testid="spin-signin-cta" className="mt-2 inline-flex items-center gap-1.5 bg-taven-black text-white text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-taven-orange transition-colors"><LogIn className="w-3.5 h-3.5" /> Sign in to claim</Link>
                    </div>
                  )}
                </div>

                {/* Featured winning product (real, available) */}
                {featured && (
                  <div className="mt-3 flex items-center gap-3 border border-black/10 p-3 text-left" data-testid="spin-featured-product">
                    <img src={featured.image} alt="" className="w-16 h-16 object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold line-clamp-2 leading-tight">{featured.title}</p>
                      <p className="text-sm text-taven-orange font-black mt-0.5">${featured.price?.toFixed(2)}
                        {result.type === "DISCOUNT" && <span className="text-taven-gold font-bold ml-2">−{result.percent}% with your prize</span>}
                      </p>
                    </div>
                  </div>
                )}

                {!result.requires_login && (
                  <button onClick={claimPrize} disabled={claiming} data-testid="spin-claim-btn"
                    className="w-full mt-3 flex items-center justify-center gap-2 bg-taven-orange text-white font-bold uppercase tracking-wider py-4 hover:bg-[#d9561a] transition-colors disabled:opacity-60">
                    {claiming ? "Claiming…" : "Claim My Prize"} <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <p className="text-xs font-bold uppercase tracking-wider text-taven-gold mt-3 flex items-center justify-center gap-1.5" data-testid="spin-streak">
                  <Flame className="w-3.5 h-3.5" /> Spin again tomorrow — {result.streak}-day streak
                </p>
                <button onClick={close} className="mt-3 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-black">Continue Shopping</button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-4">Discounts apply to products only. Not valid on certificates, affiliate or seller plans. Coins &amp; class rewards require a free TAVEN account. Support: support@taven.store</p>
          </div>
        </div>
      )}
    </>
  );
};

export default SpinWheel;
